import { SaveManager, type WorldState } from '../../../state/SaveManager.js';
import {
  NUMBER_KINGDOM_DISTRICTS,
  isNumberKingdomDistrict,
  type NumberKingdomDistrict,
} from './districts/NumberKingdomDistrictRegistry.js';

export {
  NUMBER_KINGDOM_DISTRICTS,
  type NumberKingdomDistrict,
} from './districts/NumberKingdomDistrictRegistry.js';

export const NUMBER_KINGDOM_WORLD_ID = 'number-kingdom';

export type AdditionCitadelSubArea = 'twin-towers' | 'number-forge' | 'regrouping-workshop';

export type StarBridgeState =
  | 'broken'
  | 'repair-1'
  | 'repair-2'
  | 'repair-3'
  | 'restored';

export interface NumberKingdomStateSnapshot {
  visited: boolean;
  currentDistrict: NumberKingdomDistrict;
  starBridgeDiscovered: boolean;
  starBridgeState: StarBridgeState;
  bridgeCrossingUnlocked: boolean;
  districtUnlocks: readonly NumberKingdomDistrict[];
  additionCitadelDiscovered: boolean;
  additionCitadelSubArea: AdditionCitadelSubArea;
  numberForgeDiscovered: boolean;
  numberForgeChallengesCompleted: readonly string[];
  regroupingWorkshopEligible: boolean;
  regroupingWorkshopDiscovered: boolean;
  regroupingWorkshopChallengesStarted: readonly string[];
  regroupingWorkshopChallengesCompleted: readonly string[];
  regroupingWorkshopPowered: boolean;
  regroupingWorkshopSnapshots: Readonly<Record<string, unknown>>;
  additionCitadelMechanismsPowered: readonly string[];
  districtCompletion: Readonly<Partial<Record<NumberKingdomDistrict, boolean>>>;
  districtProgress: Readonly<Record<string, unknown>>;
}

type NumberKingdomWorldState = WorldState & {
  bridgeCrossingUnlocked?: boolean;
  districtUnlocks?: NumberKingdomDistrict[];
  additionCitadelDiscovered?: boolean;
  additionCitadelSubArea?: AdditionCitadelSubArea;
  numberForgeDiscovered?: boolean;
  numberForgeChallengesCompleted?: string[];
  regroupingWorkshopDiscovered?: boolean;
  regroupingWorkshopChallengesStarted?: string[];
  regroupingWorkshopChallengesCompleted?: string[];
  regroupingWorkshopSnapshots?: Record<string, unknown>;
  additionCitadelMechanismsPowered?: string[];
  districtCompletion?: Partial<Record<NumberKingdomDistrict, boolean>>;
  numberKingdomDistrictProgress?: Record<string, unknown>;
};

function deriveStarBridgeState(world: WorldState): StarBridgeState {
  if (world.restored || world.bridgeSegments >= 4) return 'restored';
  if (world.bridgeSegments === 3) return 'repair-3';
  if (world.bridgeSegments === 2) return 'repair-2';
  if (world.bridgeSegments === 1) return 'repair-1';
  return 'broken';
}

export function getNumberKingdomState(): NumberKingdomStateSnapshot {
  const world = getNumberKingdomWorld();
  const starBridgeState = deriveStarBridgeState(world);
  const district = isNumberKingdomDistrict(world.currentDistrict)
    ? world.currentDistrict
    : NUMBER_KINGDOM_DISTRICTS.arrivalValley;
  const districtUnlocks = getDerivedDistrictUnlocks(world, starBridgeState);

  const numberForgeChallengesCompleted = [...(world.numberForgeChallengesCompleted ?? [])];
  const regroupingWorkshopChallengesCompleted = [
    ...(world.regroupingWorkshopChallengesCompleted ?? []),
  ];
  const poweredMechanisms = [...(world.additionCitadelMechanismsPowered ?? [])];

  return {
    visited: world.visited === true,
    currentDistrict: district,
    starBridgeDiscovered: world.starBridgeDiscovered === true,
    starBridgeState,
    bridgeCrossingUnlocked: world.bridgeCrossingUnlocked === true || starBridgeState === 'restored',
    districtUnlocks,
    additionCitadelDiscovered: world.additionCitadelDiscovered === true,
    additionCitadelSubArea: deriveAdditionCitadelSubArea(world),
    numberForgeDiscovered: world.numberForgeDiscovered === true,
    numberForgeChallengesCompleted,
    regroupingWorkshopEligible: numberForgeChallengesCompleted.length > 0,
    regroupingWorkshopDiscovered: world.regroupingWorkshopDiscovered === true,
    regroupingWorkshopChallengesStarted: [
      ...(world.regroupingWorkshopChallengesStarted ?? []),
    ],
    regroupingWorkshopChallengesCompleted,
    regroupingWorkshopPowered: regroupingWorkshopChallengesCompleted.length > 0
      && poweredMechanisms.some(event => event.startsWith('regrouping-workshop-powered')),
    regroupingWorkshopSnapshots: { ...(world.regroupingWorkshopSnapshots ?? {}) },
    additionCitadelMechanismsPowered: poweredMechanisms,
    districtCompletion: { ...world.districtCompletion },
    districtProgress: { ...(world.numberKingdomDistrictProgress ?? {}) },
  };
}

export function visitNumberKingdomDistrict(district: NumberKingdomDistrict) {
  const world = getNumberKingdomWorld();
  const firstVisit = world.visited !== true;
  world.visited = true;
  world.currentDistrict = district;
  addDistrictUnlock(world, district);
  SaveManager.setLastScene('NumberKingdom');
  SaveManager.save();
  return { firstVisit, state: getNumberKingdomState() };
}

export function markStarBridgeDiscovered() {
  const world = getNumberKingdomWorld();
  world.starBridgeDiscovered = true;
  world.currentDistrict = NUMBER_KINGDOM_DISTRICTS.starBridge;
  addDistrictUnlock(world, NUMBER_KINGDOM_DISTRICTS.starBridge);
  SaveManager.setLastScene('NumberKingdom');
  SaveManager.save();
  return getNumberKingdomState();
}

export function completeFirstStarBridgeRepair() {
  const world = getNumberKingdomWorld();
  world.bridgeSegments = Math.max(1, world.bridgeSegments);
  world.currentDistrict = NUMBER_KINGDOM_DISTRICTS.starBridge;
  SaveManager.setLastScene('NumberKingdom');
  SaveManager.save();
  return getNumberKingdomState();
}

export function completeSecondStarBridgeRepair() {
  const world = getNumberKingdomWorld();
  world.bridgeSegments = Math.max(2, world.bridgeSegments);
  world.currentDistrict = NUMBER_KINGDOM_DISTRICTS.starBridge;
  SaveManager.setLastScene('NumberKingdom');
  SaveManager.save();
  return getNumberKingdomState();
}

export function completeStarBridgeRestoration() {
  const world = getNumberKingdomWorld();
  world.bridgeSegments = Math.max(3, world.bridgeSegments);
  world.restored = true;
  world.bridgeCrossingUnlocked = true;
  world.currentDistrict = NUMBER_KINGDOM_DISTRICTS.starBridge;
  addDistrictUnlock(world, NUMBER_KINGDOM_DISTRICTS.kingdomCrossroads);
  SaveManager.setLastScene('NumberKingdom');
  SaveManager.save();
  return getNumberKingdomState();
}

export function unlockNumberKingdomDistrict(district: NumberKingdomDistrict) {
  const world = getNumberKingdomWorld();
  addDistrictUnlock(world, district);
  SaveManager.save();
  return getNumberKingdomState();
}

export function markAdditionCitadelDiscovered() {
  const world = getNumberKingdomWorld();
  world.additionCitadelDiscovered = true;
  addDistrictUnlock(world, NUMBER_KINGDOM_DISTRICTS.additionCitadel);
  SaveManager.save();
  return getNumberKingdomState();
}

export function completeNumberKingdomDistrict(district: NumberKingdomDistrict) {
  const world = getNumberKingdomWorld();
  world.districtCompletion = { ...world.districtCompletion, [district]: true };
  addDistrictUnlock(world, district);
  SaveManager.save();
  return getNumberKingdomState();
}

export function saveNumberKingdomDistrictProgress(
  district: NumberKingdomDistrict,
  snapshot: unknown,
) {
  const world = getNumberKingdomWorld();
  world.numberKingdomDistrictProgress = {
    ...(world.numberKingdomDistrictProgress ?? {}),
    [district]: snapshot,
  };
  world.currentDistrict = district;
  SaveManager.save();
  return getNumberKingdomState();
}

export function completeNumberKingdomDistrictProgress(
  district: NumberKingdomDistrict,
  snapshot: unknown,
  nextDistrict?: NumberKingdomDistrict,
) {
  const world = getNumberKingdomWorld();
  world.numberKingdomDistrictProgress = {
    ...(world.numberKingdomDistrictProgress ?? {}),
    [district]: snapshot,
  };
  world.districtCompletion = { ...world.districtCompletion, [district]: true };
  addDistrictUnlock(world, district);
  if (nextDistrict) addDistrictUnlock(world, nextDistrict);
  world.currentDistrict = district;
  SaveManager.save();
  return getNumberKingdomState();
}

export function markNumberForgeDiscovered() {
  const world = getNumberKingdomWorld();
  world.numberForgeDiscovered = true;
  world.additionCitadelSubArea = 'number-forge';
  world.currentDistrict = NUMBER_KINGDOM_DISTRICTS.additionCitadel;
  SaveManager.setLastScene('NumberForge');
  SaveManager.save();
  return getNumberKingdomState();
}

export function completeNumberForgeProgress(challengeId: string, worldEvent: string) {
  if (!challengeId.trim() || !worldEvent.trim()) {
    throw new RangeError('Number Forge progression identifiers must not be empty.');
  }

  const world = getNumberKingdomWorld();
  const completed = new Set(world.numberForgeChallengesCompleted ?? []);
  const powered = new Set(world.additionCitadelMechanismsPowered ?? []);
  const firstCompletion = !completed.has(challengeId);

  completed.add(challengeId);
  powered.add(worldEvent);
  world.numberForgeDiscovered = true;
  world.additionCitadelSubArea = 'number-forge';
  world.currentDistrict = NUMBER_KINGDOM_DISTRICTS.additionCitadel;
  world.numberForgeChallengesCompleted = [...completed];
  world.additionCitadelMechanismsPowered = [...powered];
  SaveManager.setLastScene('NumberForge');
  SaveManager.save();
  return { firstCompletion, state: getNumberKingdomState() };
}

export function markRegroupingWorkshopDiscovered() {
  const current = getNumberKingdomState();
  if (!current.regroupingWorkshopEligible) return current;

  const world = getNumberKingdomWorld();
  world.regroupingWorkshopDiscovered = true;
  world.additionCitadelSubArea = 'regrouping-workshop';
  world.currentDistrict = NUMBER_KINGDOM_DISTRICTS.additionCitadel;
  SaveManager.setLastScene('AdditionCitadel');
  SaveManager.save();
  return getNumberKingdomState();
}

export function startRegroupingWorkshopProgress(challengeId: string) {
  if (!challengeId.trim()) {
    throw new RangeError('Regrouping Workshop challenge identifier must not be empty.');
  }
  const current = getNumberKingdomState();
  if (!current.regroupingWorkshopEligible) {
    return { newlyStarted: false, state: current };
  }

  const world = getNumberKingdomWorld();
  const started = new Set(world.regroupingWorkshopChallengesStarted ?? []);
  const newlyStarted = !started.has(challengeId);
  started.add(challengeId);
  world.regroupingWorkshopDiscovered = true;
  world.regroupingWorkshopChallengesStarted = [...started];
  world.additionCitadelSubArea = 'regrouping-workshop';
  world.currentDistrict = NUMBER_KINGDOM_DISTRICTS.additionCitadel;
  SaveManager.setLastScene('AdditionCitadel');
  SaveManager.save();
  return { newlyStarted, state: getNumberKingdomState() };
}

export function saveRegroupingWorkshopSnapshot(challengeId: string, snapshot: unknown) {
  if (!challengeId.trim()) {
    throw new RangeError('Regrouping Workshop challenge identifier must not be empty.');
  }
  const world = getNumberKingdomWorld();
  world.regroupingWorkshopSnapshots = {
    ...(world.regroupingWorkshopSnapshots ?? {}),
    [challengeId]: snapshot,
  };
  SaveManager.save();
  return getNumberKingdomState();
}

export function completeRegroupingWorkshopProgress(challengeId: string, worldEvent: string) {
  if (!challengeId.trim() || !worldEvent.trim()) {
    throw new RangeError('Regrouping Workshop progression identifiers must not be empty.');
  }
  const current = getNumberKingdomState();
  if (!current.regroupingWorkshopEligible) {
    return { firstCompletion: false, state: current };
  }

  const world = getNumberKingdomWorld();
  const started = new Set(world.regroupingWorkshopChallengesStarted ?? []);
  const completed = new Set(world.regroupingWorkshopChallengesCompleted ?? []);
  const powered = new Set(world.additionCitadelMechanismsPowered ?? []);
  const firstCompletion = !completed.has(challengeId);

  started.add(challengeId);
  completed.add(challengeId);
  powered.add(worldEvent);
  world.regroupingWorkshopDiscovered = true;
  world.regroupingWorkshopChallengesStarted = [...started];
  world.regroupingWorkshopChallengesCompleted = [...completed];
  world.additionCitadelMechanismsPowered = [...powered];
  addDistrictUnlock(world, NUMBER_KINGDOM_DISTRICTS.subtractionCaverns);
  world.additionCitadelSubArea = 'regrouping-workshop';
  world.currentDistrict = NUMBER_KINGDOM_DISTRICTS.additionCitadel;
  SaveManager.setLastScene('AdditionCitadel');
  SaveManager.save();
  return { firstCompletion, state: getNumberKingdomState() };
}

function getNumberKingdomWorld() {
  return SaveManager.getWorld(NUMBER_KINGDOM_WORLD_ID) as NumberKingdomWorldState;
}

function addDistrictUnlock(world: NumberKingdomWorldState, district: NumberKingdomDistrict) {
  const existing = (world.districtUnlocks ?? []).filter(isNumberKingdomDistrict);
  world.districtUnlocks = existing.includes(district) ? existing : [...existing, district];
}

function deriveAdditionCitadelSubArea(world: NumberKingdomWorldState): AdditionCitadelSubArea {
  if (world.additionCitadelSubArea === 'regrouping-workshop'
    && world.regroupingWorkshopDiscovered) return 'regrouping-workshop';
  return world.additionCitadelSubArea === 'number-forge' || world.numberForgeDiscovered
    ? 'number-forge'
    : 'twin-towers';
}

function getDerivedDistrictUnlocks(
  world: NumberKingdomWorldState,
  starBridgeState: StarBridgeState,
) {
  const unlocks = new Set<NumberKingdomDistrict>([
    NUMBER_KINGDOM_DISTRICTS.arrivalValley,
    ...(world.districtUnlocks ?? []).filter(isNumberKingdomDistrict),
  ]);
  if (world.starBridgeDiscovered) unlocks.add(NUMBER_KINGDOM_DISTRICTS.starBridge);
  if (starBridgeState === 'restored') unlocks.add(NUMBER_KINGDOM_DISTRICTS.kingdomCrossroads);
  if (world.additionCitadelDiscovered) unlocks.add(NUMBER_KINGDOM_DISTRICTS.additionCitadel);
  if ((world.regroupingWorkshopChallengesCompleted?.length ?? 0) > 0
    && (world.additionCitadelMechanismsPowered ?? [])
      .some(event => event.startsWith('regrouping-workshop-powered'))) {
    unlocks.add(NUMBER_KINGDOM_DISTRICTS.subtractionCaverns);
  }
  return [...unlocks];
}
